// src/pages/network/NetworkProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft, MapPin, Globe, Mail, Phone, Users, Building,
  Calendar, LinkIcon, UserPlus, MessageSquare, Share2, MoreHorizontal,
  Clock, AlertCircle, Check, Copy
} from 'lucide-react';
import PostList from '../../components/network/feed/PostList';
import ConnectionStatus from '../../components/network/connections/ConnectionStatus';
import { db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

const NetworkProfile = () => {
  const { businessId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('none'); // 'none', 'pending', 'pending_received', 'accepted'
  const [activeTab, setActiveTab] = useState('posts');
  const [profileConnections, setProfileConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionCount, setConnectionCount] = useState(0);
  const [mutualConnections, setMutualConnections] = useState([]);
  const [peopleAlsoViewed, setPeopleAlsoViewed] = useState([]);
  const [shareCopied, setShareCopied] = useState(false);

  // Fetch business profile and posts
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch business profile
        const profileRef = doc(db, 'businesses', businessId);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
          setError('Business profile not found');
          setLoading(false);
          return;
        }

        const profileData = {
          id: profileSnap.id,
          ...profileSnap.data()
        };
        setProfile(profileData);

        // Fetch posts by this business
        const postsQuery = query(
          collection(db, 'posts'),
          where('authorId', '==', businessId),
          orderBy('createdAt', 'desc'),
          limit(10)
        );

        const postsSnap = await getDocs(postsQuery);
        const postsData = [];

        postsSnap.forEach((doc) => {
          const data = doc.data();
          postsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date()
          });
        });

        setPosts(postsData);

        // Fetch connection count for this business
        const connCountQuery = query(
          collection(db, 'connections'),
          where('participants', 'array-contains', businessId),
          where('status', '==', 'accepted')
        );
        const connCountSnap = await getDocs(connCountQuery);
        setConnectionCount(connCountSnap.size);

        // Check connection status if user is logged in
        if (currentUser && currentUser.uid !== businessId) {
          const connectionQuery = query(
            collection(db, 'connections'),
            where('participants', 'array-contains', currentUser.uid),
            where('status', 'in', ['pending', 'accepted'])
          );

          const connectionSnap = await getDocs(connectionQuery);
          let found = false;

          connectionSnap.forEach(doc => {
            const data = doc.data();
            if ((data.requesterId === currentUser.uid && data.receiverId === businessId) ||
                (data.requesterId === businessId && data.receiverId === currentUser.uid)) {
              if (data.status === 'accepted') {
                setConnectionStatus('accepted');
              } else if (data.status === 'pending') {
                // Distinguish sent vs received
                if (data.requesterId === currentUser.uid) {
                  setConnectionStatus('pending');
                } else {
                  setConnectionStatus('pending_received');
                }
              }
              found = true;
            }
          });

          if (!found) {
            setConnectionStatus('none');
          }

          // Find mutual connections
          const myConnectionsQuery = query(
            collection(db, 'connections'),
            where('participants', 'array-contains', currentUser.uid),
            where('status', '==', 'accepted')
          );
          const myConnsSnap = await getDocs(myConnectionsQuery);
          const myConnectedIds = new Set();
          myConnsSnap.forEach(d => {
            const cd = d.data();
            const otherId = cd.requesterId === currentUser.uid ? cd.receiverId : cd.requesterId;
            myConnectedIds.add(otherId);
          });

          // Get business's connections
          const theirIds = new Set();
          connCountSnap.forEach(d => {
            const cd = d.data();
            const otherId = cd.requesterId === businessId ? cd.receiverId : cd.requesterId;
            theirIds.add(otherId);
          });

          const mutualIds = [...myConnectedIds].filter(id => theirIds.has(id));
          if (mutualIds.length > 0) {
            const mutualData = [];
            for (const mid of mutualIds.slice(0, 5)) {
              try {
                const bSnap = await getDoc(doc(db, 'businesses', mid));
                if (bSnap.exists()) {
                  mutualData.push({ id: bSnap.id, ...bSnap.data() });
                }
              } catch (e) { /* skip */ }
            }
            setMutualConnections(mutualData);
          }
        }

        // Fetch "People Also Viewed" - same industry businesses
        if (profileData.industry) {
          try {
            const sameIndustryQuery = query(
              collection(db, 'businesses'),
              where('industry', '==', profileData.industry),
              limit(6)
            );
            const sameSnap = await getDocs(sameIndustryQuery);
            const similar = [];
            sameSnap.forEach(d => {
              if (d.id !== businessId) {
                similar.push({ id: d.id, ...d.data() });
              }
            });
            setPeopleAlsoViewed(similar.slice(0, 3));
          } catch (e) {
            console.error('Error fetching similar businesses:', e);
          }
        }

      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [businessId, currentUser]);

  // Fetch connections when tab is selected
  useEffect(() => {
    if (activeTab !== 'connections') return;

    const fetchConnections = async () => {
      try {
        setConnectionsLoading(true);
        const connectionsQuery = query(
          collection(db, 'connections'),
          where('participants', 'array-contains', businessId),
          where('status', '==', 'accepted')
        );

        const connSnap = await getDocs(connectionsQuery);
        const connList = [];
        const businessIds = new Set();

        connSnap.forEach(d => {
          const data = d.data();
          const otherId = data.requesterId === businessId ? data.receiverId : data.requesterId;
          connList.push({ id: d.id, ...data, otherPartyId: otherId });
          businessIds.add(otherId);
        });

        // Fetch business info
        const businessData = {};
        for (const bid of businessIds) {
          try {
            const bSnap = await getDoc(doc(db, 'businesses', bid));
            if (bSnap.exists()) {
              businessData[bid] = { id: bSnap.id, ...bSnap.data() };
            }
          } catch (e) { /* skip */ }
        }

        setProfileConnections(connList.map(c => ({
          ...c,
          business: businessData[c.otherPartyId] || null
        })));
      } catch (err) {
        console.error('Error fetching connections:', err);
      } finally {
        setConnectionsLoading(false);
      }
    };

    fetchConnections();
  }, [activeTab, businessId]);

  // Handle Message button click
  const handleMessageClick = async () => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network/profile/${businessId}` } });
      return;
    }

    try {
      // Check for existing conversation
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', currentUser.uid)
      );
      const convSnap = await getDocs(conversationsQuery);
      let existingConvId = null;

      convSnap.forEach(d => {
        const data = d.data();
        if (data.participants.includes(businessId)) {
          existingConvId = d.id;
        }
      });

      if (existingConvId) {
        navigate(`/network/messages/${existingConvId}`);
      } else {
        navigate('/network/messages', { state: { recipientId: businessId, recipientName: profile?.name } });
      }
    } catch (err) {
      console.error('Error checking conversations:', err);
      navigate('/network/messages', { state: { recipientId: businessId, recipientName: profile?.name } });
    }
  };

  // Handle Share button
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/network/profile/${businessId}`;
    const shareData = {
      title: profile?.name || 'Business Profile',
      text: `Check out ${profile?.name} on BizCentral Business Network`,
      url: shareUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8 text-center">
        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-medium text-gray-900 mb-2">Profile Not Found</h2>
        <p className="text-gray-500 mb-6">The business profile you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/network"
          className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Network
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4">
      <div className="mb-6">
        <Link
          to="/network"
          className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Network
        </Link>
      </div>

      {/* Profile Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        {/* Cover Image */}
        <div className="h-48 bg-gradient-to-r from-[#190192] to-[#3B0DB5] relative">
          {profile.coverImageUrl && (
            <img
              src={profile.coverImageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute left-6 -bottom-16 w-32 h-32 border-4 border-white rounded-xl bg-white shadow-sm overflow-hidden">
            {profile.businessLogoUrl ? (
              <img
                src={profile.businessLogoUrl}
                alt={profile.name}
                className="w-full h-full object-contain p-1"
              />
            ) : (
              <div className="w-full h-full bg-purple-100 flex items-center justify-center text-[#190192]">
                <Building size={40} />
              </div>
            )}
          </div>
        </div>

        {/* Profile Info */}
        <div className="pt-20 px-6 pb-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{profile.name}</h1>
                {connectionCount > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-[#190192]">
                    <Users size={12} className="mr-1" />
                    {connectionCount} connection{connectionCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p className="text-gray-600">{profile.industry || 'Business'}</p>

              {/* Mutual connections */}
              {mutualConnections.length > 0 && currentUser && currentUser.uid !== businessId && (
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <Users size={14} className="mr-1.5 text-gray-400" />
                  <span>
                    {mutualConnections.length} mutual connection{mutualConnections.length !== 1 ? 's' : ''}:
                    {' '}
                    {mutualConnections.slice(0, 2).map((mc, i) => (
                      <span key={mc.id}>
                        <Link to={`/network/profile/${mc.id}`} className="text-[#190192] hover:underline">
                          {mc.name}
                        </Link>
                        {i < Math.min(mutualConnections.length, 2) - 1 ? ', ' : ''}
                      </span>
                    ))}
                    {mutualConnections.length > 2 && ` and ${mutualConnections.length - 2} more`}
                  </span>
                </div>
              )}

              <div className="mt-4 space-y-2">
                {profile.address && (
                  <div className="flex items-center text-gray-600">
                    <MapPin size={16} className="mr-2 text-gray-400" />
                    <span>{profile.address}</span>
                  </div>
                )}

                {profile.website && (
                  <div className="flex items-center text-gray-600">
                    <Globe size={16} className="mr-2 text-gray-400" />
                    <a
                      href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#190192] hover:underline"
                    >
                      {profile.website}
                    </a>
                  </div>
                )}

                {profile.email && (
                  <div className="flex items-center text-gray-600">
                    <Mail size={16} className="mr-2 text-gray-400" />
                    <a
                      href={`mailto:${profile.email}`}
                      className="text-[#190192] hover:underline"
                    >
                      {profile.email}
                    </a>
                  </div>
                )}

                {profile.phone && (
                  <div className="flex items-center text-gray-600">
                    <Phone size={16} className="mr-2 text-gray-400" />
                    <a
                      href={`tel:${profile.phone}`}
                      className="text-[#190192] hover:underline"
                    >
                      {profile.phone}
                    </a>
                  </div>
                )}

                <div className="flex items-center text-gray-600">
                  <Calendar size={16} className="mr-2 text-gray-400" />
                  <span>Joined {profile.createdAt ? new Date(profile.createdAt.seconds * 1000).toLocaleDateString() : 'recently'}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
              {/* Show connection button only if viewing someone else's profile */}
              {currentUser && currentUser.uid !== businessId && (
                <ConnectionStatus
                  businessId={businessId}
                  status={connectionStatus}
                  onStatusChange={(newStatus) => setConnectionStatus(newStatus)}
                />
              )}

              <button
                onClick={handleMessageClick}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center"
              >
                <MessageSquare size={16} className="mr-2" />
                Message
              </button>

              <button
                onClick={handleShare}
                className="p-2 text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 relative"
              >
                {shareCopied ? <Check size={18} className="text-green-500" /> : <Share2 size={18} />}
              </button>

              <button className="p-2 text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50">
                <MoreHorizontal size={18} />
              </button>
            </div>
          </div>

          {/* Share copied toast */}
          {shareCopied && (
            <div className="mt-2 inline-flex items-center text-sm text-green-600 bg-green-50 px-3 py-1 rounded-lg">
              <Copy size={14} className="mr-1" />
              Link copied to clipboard!
            </div>
          )}

          {/* About Section */}
          {profile.description && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="text-lg font-medium text-gray-900 mb-2">About</h3>
              <p className="text-gray-700 whitespace-pre-line">{profile.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Profile Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('posts')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'posts'
                  ? 'border-[#190192] text-[#190192]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Posts
            </button>
            <button
              onClick={() => setActiveTab('about')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'about'
                  ? 'border-[#190192] text-[#190192]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              About
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'connections'
                  ? 'border-[#190192] text-[#190192]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Connections
              {connectionCount > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
                  {connectionCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Contents */}
        <div className="p-6">
          {activeTab === 'posts' && (
            <div>
              {posts.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare size={48} className="mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No posts yet</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {currentUser && currentUser.uid === businessId
                      ? "You haven't shared any posts yet. Start sharing updates, news, or insights with the network!"
                      : `${profile.name} hasn't shared any posts yet.`}
                  </p>
                </div>
              ) : (
                <PostList
                  posts={posts}
                  loading={false}
                  hasMore={false}
                  isAuthenticated={!!currentUser}
                />
              )}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="max-w-3xl">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Industry</h4>
                    <p className="text-gray-900">{profile.industry || 'Not specified'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Type</h4>
                    <p className="text-gray-900">{profile.type || 'Not specified'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Size</h4>
                    <p className="text-gray-900">{profile.employees ? `${profile.employees} employees` : 'Not specified'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Founded</h4>
                    <p className="text-gray-900">{profile.founded || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {profile.description && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">About {profile.name}</h3>
                  <p className="text-gray-700 whitespace-pre-line">{profile.description}</p>
                </div>
              )}

              {profile.specialties && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Specialties</h3>
                  <p className="text-gray-700">{profile.specialties}</p>
                </div>
              )}

              {/* Social Links Section */}
              {(profile.socialLinkUrls && Object.keys(profile.socialLinkUrls).length > 0) && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Social Profiles</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(profile.socialLinkUrls).map(([platform, url]) => (
                      <a
                        key={platform}
                        href={url.startsWith('http') ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 inline-flex items-center"
                      >
                        <LinkIcon size={16} className="mr-2" />
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'connections' && (
            <div>
              {connectionsLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
                </div>
              ) : profileConnections.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No connections yet</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {profile.name} hasn't made any connections yet.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profileConnections.map(connection => (
                    <Link
                      key={connection.id}
                      to={`/network/profile/${connection.otherPartyId}`}
                      className="border border-gray-200 rounded-xl p-4 flex items-center hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                        {connection.business?.businessLogoUrl ? (
                          <img
                            src={connection.business.businessLogoUrl}
                            alt={connection.business?.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                            <Building size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-medium truncate">
                          {connection.business?.name || 'Unknown Business'}
                        </p>
                        <p className="text-gray-500 text-sm truncate">
                          {connection.business?.industry || 'Business'}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* People Also Viewed */}
      {peopleAlsoViewed.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">People Also Viewed</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {peopleAlsoViewed.map(biz => (
                <Link
                  key={biz.id}
                  to={`/network/profile/${biz.id}`}
                  className="border border-gray-200 rounded-xl p-4 flex items-center hover:bg-gray-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                    {biz.businessLogoUrl ? (
                      <img src={biz.businessLogoUrl} alt={biz.name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                        <Building size={20} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-medium truncate">{biz.name}</p>
                    <p className="text-gray-500 text-sm truncate">{biz.industry || 'Business'}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkProfile;
